import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import { BuiltInExteriorPropId } from "building-types/props/builtInExteriorPropId";
import { PropId } from "building-types/props/propIds";
import type { HomeSettings } from "building-types/settings/homeSettings";
import type { PorchSettings } from "building-types/settings/porchSettings";
import type { RearPorchSettings } from "building-types/settings/rearPorchSettings";
import { EaveBracketSide } from "building-types/settings/roofBaseTypes";
import type { RoofSettings } from "building-types/settings/roofSettings";
import { SettingsType } from "building-types/settings/settingsType";
import {
  ColonnadeTerminationType,
  WallIntersectionType,
  WrapFriezeBoardMode,
  EaveType,
  FasciaTopTrimSize,
  ShutterType,
  ApronCasingType,
  SideCasingType,
  StoolCasingType,
  WindowMullionType,
} from "building-types/settings/settingsTypesEnum";
import { defaultExteriorPropCatalog } from "../elements/propCatalog/exteriorPropCatalog";
import { fasciaTopTrimSizeToValue } from "../fasciaTopTrimSizeToValue";
import { headCasingJackArchNew, headCasingNoneNew, headCasingSlicesNew } from "../settingsTools";
import { BuiltinHomeSettingsId, builtinHomeSettingsPrettyName } from "./builtinHomeSettingsId";
import {
  defaultColumnSettings,
  defaultExteriorDoorCasingSettings,
  defaultExteriorWindowCasingSettings,
  defaultFarmhouseWindowSettings,
  defaultFriezeBoardSettings,
  defaultHomeSettings,
  defaultPorchSettings,
  defaultWainscotingSettings,
  defaultWindowSettings,
} from "./defaultSettings";

type WindowSettings = Pick<HomeSettings, "windowMullions" | "windowShutters" | "exteriorWindowCasing">;

const smithDouglasRoofSettings: RoofSettings = {
  type: SettingsType.ROOF_SETTINGS,
  eaveOverhang: 12,
  rakeOverhang: 12,
  shedOverhang: 8,
  eaveType: EaveType.BOXED_BOX,
  eaveReturnDistance: 4,
  eaveExtensionAtCoplanarRake: 4,
  eaveExtensionAtNonCoplanarRake: 0,
  eaveExtensionAtCoplanarWall: 1.75,
  rakeExtensionAtCoplanarWall: 0,
  friezeBoardOrientation: StreetOrientationMode.FRONT_FACES,
  includeFasciaTopTrim: true,
  fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
  eaveFasciaSize: 8,
  rakeFasciaSize: 8,
  rakeFriezeBoard: defaultFriezeBoardSettings,
  includeFriezeBoardOnPorches: true,
  roofFramingSpacing: 24,
  lookoutRafterSpacing: 24,
  includeFlyRafters: true,
  includeLookoutRafters: true,
  includeStickFramingAtStart: true,
  webChordSpacingAtStart: 16,
  includeStickFramingAtEnd: true,
  webChordSpacingAtEnd: 16,
  setBackForStepDownTruss: 72,
  generateRoofFraming: false,
};

const smithDouglasWindowSettingsC: WindowSettings = {
  ...defaultFarmhouseWindowSettings,
  ...defaultWindowSettings,
  exteriorWindowCasing: {
    ...defaultExteriorWindowCasingSettings,
    casingOrientation: StreetOrientationMode.FRONT_FACES,
    ...headCasingSlicesNew(
      [
        { height: 6, depthOffset: 1, widthOffset: 0, material: "headCasing1" },
        { height: 1, depthOffset: 1.15, widthOffset: 1, material: "headCasing1" },
        { height: 1, depthOffset: 1.25, widthOffset: 1, material: "headCasing1" },
      ],
      false,
    ),
    sideCasingType: SideCasingType.DISABLED,
    stoolCasingType: StoolCasingType.DISABLED,
    apronCasingType: ApronCasingType.DISABLED,
  },
  windowShutters: {
    type: SettingsType.WINDOW_SHUTTER_SETTINGS,
    shutterType: ShutterType.SIMPLE_SLATS,
    shutterWidthOverride: 12,
  },
  windowMullions: {
    type: SettingsType.WINDOW_MULLION_SETTINGS,
    mullionType: WindowMullionType.ORTHOGONAL,
    mullionOrientation: StreetOrientationMode.FRONT_FACES,
    paneOneXCount: 3,
    paneOneYCount: 2,
    paneTwoYCount: 0,
    paneTwoXCount: 0,
    verticalThickness: 1,
    horizontalThickness: 1,
  },
};

const smithDouglasWindowSettingsE: WindowSettings = {
  ...defaultFarmhouseWindowSettings,
  ...defaultWindowSettings,
  exteriorWindowCasing: {
    ...defaultExteriorWindowCasingSettings,
    sideCasingType: SideCasingType.DISABLED,
    stoolCasingType: StoolCasingType.DISABLED,
    apronCasingType: ApronCasingType.DISABLED,
    casingOrientation: StreetOrientationMode.FRONT_FACES,
  },
  windowShutters: {
    type: SettingsType.WINDOW_SHUTTER_SETTINGS,
    shutterType: ShutterType.DISABLED,
  },
  windowMullions: {
    type: SettingsType.WINDOW_MULLION_SETTINGS,
    mullionType: WindowMullionType.ORTHOGONAL,
    paneOneXCount: 3,
    paneOneYCount: 2,
    paneTwoYCount: 0,
    paneTwoXCount: 0,
    verticalThickness: 1,
    horizontalThickness: 1,
    mullionOrientation: StreetOrientationMode.FRONT_FACES,
  },
};

const smithDouglasWindowSettingsJ: WindowSettings = {
  ...defaultFarmhouseWindowSettings,

  ...defaultWindowSettings,
  exteriorWindowCasing: {
    ...defaultExteriorWindowCasingSettings,
    sideCasingType: SideCasingType.TRIM,
    stoolCasingType: StoolCasingType.DISABLED,
    apronCasingType: ApronCasingType.TRIM,
    casingOrientation: StreetOrientationMode.FRONT_FACES,
  },
  windowShutters: {
    type: SettingsType.WINDOW_SHUTTER_SETTINGS,
    shutterType: ShutterType.DISABLED,
  },
  windowMullions: {
    type: SettingsType.WINDOW_MULLION_SETTINGS,
    mullionType: WindowMullionType.ORTHOGONAL,
    paneOneXCount: 3,
    paneOneYCount: 2,
    paneTwoYCount: 0,
    paneTwoXCount: 0,
    verticalThickness: 1,
    horizontalThickness: 1,
    mullionOrientation: StreetOrientationMode.FRONT_FACES,
  },
};

const smithDouglasWindowSettingsG: WindowSettings = {
  ...defaultFarmhouseWindowSettings,
  ...defaultWindowSettings,
  exteriorWindowCasing: {
    ...defaultExteriorWindowCasingSettings,
    ...headCasingJackArchNew(),
    sideCasingType: SideCasingType.TRIM,
    stoolCasingType: StoolCasingType.DISABLED,
    apronCasingType: ApronCasingType.TRIM,
    sideCasingWidth: 0,
    casingOrientation: StreetOrientationMode.FRONT_FACES,
  },
  windowShutters: {
    type: SettingsType.WINDOW_SHUTTER_SETTINGS,
    shutterType: ShutterType.DISABLED,
  },
  windowMullions: {
    type: SettingsType.WINDOW_MULLION_SETTINGS,
    mullionType: WindowMullionType.ORTHOGONAL,
    paneOneXCount: 3,
    paneOneYCount: 2,
    paneTwoYCount: 0,
    paneTwoXCount: 0,
    verticalThickness: 1,
    horizontalThickness: 1,
    mullionOrientation: StreetOrientationMode.FRONT_FACES,
  },
};

const smithDouglasPorchSettings: PorchSettings = {
  ...defaultPorchSettings,
  maxColumnSpacing: 240,
  doorPathColumnSpread: 60,
  columnId: "g3e_q2K5Q_h1",
  includeRailing: false,
};

const smithDouglasRearPorchSettings: RearPorchSettings = {
  ...defaultPorchSettings,
  type: SettingsType.REAR_PORCH_SETTINGS,
  maxColumnSpacing: 180,
  doorPathColumnSpread: 60,
  columnId: "56epq_KYQgi7",
  colonnadeTerminationRules: [
    {
      endType: WallIntersectionType.TEE,
      terminationType: ColonnadeTerminationType.NO_COLUMN,
    },
    {
      endType: WallIntersectionType.CORNER_REVERSE,
      terminationType: ColonnadeTerminationType.NO_COLUMN,
    },
    {
      endType: WallIntersectionType.CORNER,
      terminationType: ColonnadeTerminationType.NO_COLUMN,
    },
    {
      endType: WallIntersectionType.LINE,
      terminationType: ColonnadeTerminationType.NO_COLUMN,
    },
  ],
  includeRailing: false,
};

const smithDouglasMaterials: { [id: string]: HomeSettings } = {
  [BuiltinHomeSettingsId.SmithDouglas_B1C]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.SmithDouglas_B1C],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.SmithDouglas_B1C + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.SmithDouglas_B1C],
    },
    exteriorProps: {
      type: SettingsType.EXTERIOR_PROP_SETTINGS,
      propTypes: {
        ...defaultExteriorPropCatalog,
        garageDoorSingle: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORSINGLE],
          propAssetId: PropId.DoorGar_S8x7_4x4Pattern_01,
        },
        garageDoorDouble: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLE],
          propAssetId: PropId.DoorGar_D16x7_4x4Pattern_01,
        },
        doorSingleGlass: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASS],
          propAssetId: PropId.DoorExt_Single36_Panel6_02,
        },
        doorSingleGlassLow: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOW],
          propAssetId: PropId.DoorExt_Single36_Panel6_01,
        },
      },
    },
    roof: {
      type: SettingsType.ROOF_SETTINGS,
      eaveOverhang: 12,
      rakeOverhang: 12,
      shedOverhang: 8,
      eaveType: EaveType.BOXED_BOX,
      eaveReturnDistance: 4,
      eaveExtensionAtCoplanarRake: 4,
      eaveExtensionAtNonCoplanarRake: 0,
      eaveExtensionAtCoplanarWall: 1.75,
      rakeExtensionAtCoplanarWall: 0,
      rakeBracketParams: [48],
      rakeBracketAtPeak: true,
      eaveBracketParamsNormalized: true,
      eaveBracketParams: [0.5],
      eaveBracketSide: EaveBracketSide.BOTH,
      friezeBoardOrientation: StreetOrientationMode.FRONT_FACES,
      includeFasciaTopTrim: true,
      fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
      eaveFasciaSize: 8,
      rakeFasciaSize: 8,
      rakeFriezeBoard: defaultFriezeBoardSettings,
      includeFriezeBoardOnPorches: true,
      roofFramingSpacing: 24,
      lookoutRafterSpacing: 24,
      includeFlyRafters: true,
      includeLookoutRafters: true,
      includeStickFramingAtStart: true,
      webChordSpacingAtStart: 16,
      includeStickFramingAtEnd: true,
      webChordSpacingAtEnd: 16,
      setBackForStepDownTruss: 72,
      generateRoofFraming: false,
    },
    dormer: {
      ...defaultHomeSettings.dormer,
      eaveReturnDistance: 4,
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      height: 23,
      thickness: 2.5,
      wainscotingMode: StreetOrientationMode.FRONT_FACES,
      capHeight: 1,
    },
    ...smithDouglasWindowSettingsC,
    exteriorDoorCasing: {
      ...defaultExteriorDoorCasingSettings,
      ...headCasingSlicesNew(
        [
          { height: 6, depthOffset: 1, widthOffset: 0, material: "headCasing1" },
          { height: 1, depthOffset: 1.15, widthOffset: 1, material: "headCasing1" },
          { height: 1, depthOffset: 1.25, widthOffset: 1, material: "headCasing1" },
        ],
        false,
      ),
    },
    porch: smithDouglasPorchSettings,
    rearPorch: smithDouglasRearPorchSettings,
  },
  [BuiltinHomeSettingsId.SmithDouglas_B1E]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.SmithDouglas_B1E],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.SmithDouglas_B1E + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.SmithDouglas_B1E],
    },
    exteriorProps: {
      type: SettingsType.EXTERIOR_PROP_SETTINGS,
      propTypes: {
        ...defaultExteriorPropCatalog,
        garageDoorSingle: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORSINGLE],
          propAssetId: PropId.DoorGar_S8x7_4x4Pattern_01,
        },
        garageDoorDouble: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLE],
          propAssetId: PropId.DoorGar_D16x7_4x4Pattern_01,
        },
        doorSingleGlass: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASS],
          propAssetId: PropId.DoorExt_Single36_Panel6_02,
        },
        doorSingleGlassLow: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOW],
          propAssetId: PropId.DoorExt_Single36_Panel6_01,
        },
        doorSingleGlassSecondary: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSSECONDARY],
          propAssetId: PropId.DoorExt_Single36_Glass4_02,
        },
        doorSingleGlassLowSecondary: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOWSECONDARY],
          propAssetId: PropId.DoorExt_Single36_Glass4_01,
        },
      },
    },
    roof: smithDouglasRoofSettings,
    dormer: {
      ...defaultHomeSettings.dormer,
      eaveReturnDistance: 4,
    },
    ...smithDouglasWindowSettingsE,
    wainscoting: {
      ...defaultWainscotingSettings,
      height: 23,
      wainscotingMode: StreetOrientationMode.FRONT_FACES,
    },
    exteriorDoorCasing: {
      ...defaultExteriorDoorCasingSettings,
      ...headCasingSlicesNew(
        [
          { height: 6, depthOffset: 1, widthOffset: 0, material: "headCasing1" },
          { height: 1, depthOffset: 1.15, widthOffset: 1, material: "headCasing1" },
          { height: 1, depthOffset: 1.25, widthOffset: 1, material: "headCasing1" },
        ],
        false,
      ),
    },
    porch: smithDouglasPorchSettings,
    rearPorch: smithDouglasRearPorchSettings,

    column: {
      ...defaultColumnSettings,
    },
  },
  [BuiltinHomeSettingsId.SmithDouglas_B2J]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.SmithDouglas_B2J],
    exteriorMaterials: {
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      id: BuiltinHomeSettingsId.SmithDouglas_B2J + "_exterior",
      palettes: [BuiltinHomeSettingsId.SmithDouglas_B2J],
    },
    exteriorProps: {
      type: SettingsType.EXTERIOR_PROP_SETTINGS,
      propTypes: {
        ...defaultExteriorPropCatalog,
        garageDoorSingle: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORSINGLE],
          propAssetId: PropId.DoorGar_S8x7_2x1SlantIn_4Lite4th_H_01,
        },
        garageDoorDouble: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLE],
          propAssetId: PropId.DoorGar_D16x7_2x1SlantIn_4Lite4th_H_01,
        },
        doorSingleGlass: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASS],
          propAssetId: PropId.DoorExt_Single36_TwelveTop_02,
        },
        doorSingleGlassLow: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOW],
          propAssetId: PropId.DoorExt_Single36_TwelveTop_01,
        },
        doorSingleGlassSecondary: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSSECONDARY],
          propAssetId: PropId.DoorExt_Single36_Glass4_02,
        },
        doorSingleGlassLowSecondary: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOWSECONDARY],
          propAssetId: PropId.DoorExt_Single36_Glass4_01,
        },
      },
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      height: 0,
    },

    ...smithDouglasWindowSettingsJ,

    roof: {
      type: SettingsType.ROOF_SETTINGS,
      eaveOverhang: 12,
      rakeOverhang: 12,
      shedOverhang: 8,
      eaveType: EaveType.BOXED_BOX,
      eaveReturnDistance: 4,
      eaveExtensionAtCoplanarRake: 4,
      eaveExtensionAtNonCoplanarRake: 0,
      eaveExtensionAtCoplanarWall: 1.75,
      rakeExtensionAtCoplanarWall: 0,
      rakeBracketPropId: BuiltInExteriorPropId.RAKEBRACKET,
      rakeBracketParams: [72],
      wrapFriezeBoardMode: WrapFriezeBoardMode.FULL,
      friezeBoardOrientation: StreetOrientationMode.FRONT_FACES,
      includeFasciaTopTrim: true,
      fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
      eaveFasciaSize: 8,
      rakeFasciaSize: 8,
      rakeFriezeBoard: defaultFriezeBoardSettings,
      includeFriezeBoardOnPorches: true,
      roofFramingSpacing: 24,
      lookoutRafterSpacing: 24,
      includeFlyRafters: true,
      includeLookoutRafters: true,
      includeStickFramingAtStart: true,
      webChordSpacingAtStart: 16,
      includeStickFramingAtEnd: true,
      webChordSpacingAtEnd: 16,
      setBackForStepDownTruss: 72,
      generateRoofFraming: false,
    },
    dormer: {
      ...defaultHomeSettings.dormer,
      eaveReturnDistance: 4,
    },
    porch: smithDouglasPorchSettings,
    rearPorch: smithDouglasRearPorchSettings,

    column: {
      ...defaultColumnSettings,
    },
  },
  [BuiltinHomeSettingsId.SmithDouglas_B1G]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.SmithDouglas_B1G],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.SmithDouglas_B1G + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.SmithDouglas_B1G],
    },
    exteriorProps: {
      type: SettingsType.EXTERIOR_PROP_SETTINGS,
      propTypes: {
        ...defaultExteriorPropCatalog,
        garageDoorSingle: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORSINGLE],
          propAssetId: PropId.DoorGar_S8x7_4x4PatternHLines_01,
        },
        garageDoorDouble: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLE],
          propAssetId: PropId.DoorGar_D16x7_4x4PatternHLines_01,
        },
        doorSingleGlass: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASS],
          propAssetId: PropId.DoorExt_Single36_Panel6_02,
        },
        doorSingleGlassLow: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOW],
          propAssetId: PropId.DoorExt_Single36_Panel6_01,
        },
        doorSingleGlassSecondary: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSSECONDARY],
          propAssetId: PropId.DoorExt_Single36_02,
        },
        doorSingleGlassLowSecondary: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOWSECONDARY],
          propAssetId: PropId.DoorExt_Single36_01,
        },
      },
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      height: 0,
    },
    exteriorDoorCasing: {
      ...defaultExteriorDoorCasingSettings,
      ...headCasingNoneNew(),
    },

    ...smithDouglasWindowSettingsG,

    roof: {
      type: SettingsType.ROOF_SETTINGS,
      eaveOverhang: 12,
      rakeOverhang: 12,
      shedOverhang: 8,
      eaveType: EaveType.BOXED_BOX,
      eaveReturnDistance: 4,
      eaveExtensionAtCoplanarRake: 4,
      eaveExtensionAtNonCoplanarRake: 0,
      eaveExtensionAtCoplanarWall: 1.75,
      rakeExtensionAtCoplanarWall: 0,
      rakeBracketParams: [72],
      wrapFriezeBoardMode: WrapFriezeBoardMode.FULL,
      friezeBoardOrientation: StreetOrientationMode.FRONT_FACES,
      includeFasciaTopTrim: true,
      fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
      eaveFasciaSize: 8,
      rakeFasciaSize: 8,
      rakeFriezeBoard: defaultFriezeBoardSettings,
      includeFriezeBoardOnPorches: true,
      roofFramingSpacing: 24,
      lookoutRafterSpacing: 24,
      includeFlyRafters: true,
      includeLookoutRafters: true,
      includeStickFramingAtStart: true,
      webChordSpacingAtStart: 16,
      includeStickFramingAtEnd: true,
      webChordSpacingAtEnd: 16,
      setBackForStepDownTruss: 72,
      generateRoofFraming: false,
    },
    dormer: {
      ...defaultHomeSettings.dormer,
      eaveReturnDistance: 4,
    },
    porch: smithDouglasPorchSettings,
    rearPorch: smithDouglasRearPorchSettings,

    column: {
      ...defaultColumnSettings,
    },
  },
};

export default smithDouglasMaterials;
